/**
 * Which cards go when the viewer blocks someone (C74, Arc N): the person's own
 * cards, and albums on their trips. The second matches the server, whose feed
 * walls an album on its trip owner as well as its creator (C72), so a refetch
 * would not bring those cards back either. The album card carries the trip's
 * owner as `trip_user_id` (feedService).
 */
export const isCardFromUser = (card, userId) => {
  if (!card || userId == null) return false;
  const id = String(userId);
  const owner = card.user_id ?? card.creator_id;
  if (owner != null && String(owner) === id) return true;
  return card.content_type === 'album' && card.trip_user_id != null && String(card.trip_user_id) === id;
};

/** The list without the blocked person's cards (the same array when none go). */
export const dropCardsFromUser = (cards, userId) => {
  if (!Array.isArray(cards)) return cards;
  const kept = cards.filter(card => !isCardFromUser(card, userId));
  return kept.length === cards.length ? cards : kept;
};

/**
 * Everyone the viewer has blocked in this session. A feed or section request
 * already in flight when a block lands was answered before it, so its cards
 * include the person; the loaders pass what they commit, to screen or to a
 * module cache, through withoutBlockedUsers. Recorded by notifyUserBlocked,
 * forgotten on unblock, cleared on an auth boundary (invalidateSessionCaches).
 */
const blockedThisSession = new Set();

export const recordBlockedUser = (userId) => {
  if (userId != null) blockedThisSession.add(String(userId));
};

export const forgetBlockedUser = (userId) => {
  if (userId != null) blockedThisSession.delete(String(userId));
};

export const clearBlockedUsers = () => {
  blockedThisSession.clear();
};

/** The list without the cards of anyone blocked this session (the same array when none go). */
export const withoutBlockedUsers = (cards) => {
  if (!Array.isArray(cards) || blockedThisSession.size === 0) return cards;
  let kept = cards;
  for (const userId of blockedThisSession) kept = dropCardsFromUser(kept, userId);
  return kept;
};
